/**
 * RFC 9457 problem details, and the one place that decides how a failure is
 * shown to a person.
 *
 * # The codes are the server's, observed rather than assumed
 *
 * Every value in `ProblemCode` below was read off a real response from a
 * running `exigit-api`, and the set is `errs.Kind.String()` in
 * `internal/platform/errs/errs.go`. That matters: the first version of this
 * file guessed `unauthorized` for a 401, and the server actually sends
 * `unauthenticated`. Nothing would have noticed until somebody branched on the
 * constant.
 *
 * The contract is explicit about how to read a problem — *"Clients branch on
 * `code`, not on `title` or `detail`"*, and `code` is *"Open-ended — tolerate
 * unknown values"* — so `code` stays a `string` with the known values offered
 * as suggestions. A closed union would make a new server-side code a compile
 * error in the client, which is the coupling that note exists to prevent.
 */

/**
 * The codes this client reacts to, from `errs.Kind` and the status each maps
 * to in `internal/platform/httpx/problem.go`.
 */
export const ProblemCode = {
  /** 400 */
  InvalidArgument: 'invalid_argument',
  /** 401 — note the name: the server does **not** send `unauthorized`. */
  Unauthenticated: 'unauthenticated',
  /** 403 — also what a failed Origin/Sec-Fetch-Site check returns. */
  PermissionDenied: 'permission_denied',
  /** 403, from `internal/api/auth.go`: the session has not cleared its second factor. */
  MFARequired: 'mfa_required',
  /** 404 — "does not exist, or you do not have access to it". One answer, deliberately. */
  NotFound: 'not_found',
  /** 409 */
  Conflict: 'conflict',
  /** 412 */
  PreconditionFailed: 'precondition_failed',
  /** 422 */
  Unprocessable: 'unprocessable',
  /** 429 */
  RateLimited: 'rate_limited',
  /** 500 — the detail is a fixed generic string; the real one is in the server's log. */
  InternalError: 'internal_error',
  /** 501 */
  NotImplemented: 'not_implemented',
  /** 503 */
  Unavailable: 'unavailable',
} as const;

export type KnownProblemCode = (typeof ProblemCode)[keyof typeof ProblemCode];

/** One field-level complaint inside a 400 or 422. */
export interface ProblemError {
  /** An RFC 6901 JSON Pointer into the request body. */
  pointer?: string;
  /** Names a query or header parameter, when the fault is not in the body. */
  parameter?: string;
  /** A stable machine-readable token such as `pattern` or `enum`. */
  code?: string;
  detail?: string;
}

export interface Problem {
  type: string;
  title: string;
  status: number;
  /** Stable, snake_case, open-ended. Branch on this. */
  code: string;
  detail?: string;
  instance?: string;
  /** W3C trace id. The single most useful field in a support conversation. */
  trace_id?: string;
  errors?: ProblemError[];
}

/**
 * Every failure this client raises, including the ones that never reached the
 * server.
 *
 * A transport failure is modelled as an `ApiError` with a synthesised problem
 * rather than as a separate error type, so a caller has one shape to handle.
 * The distinction that matters is not "was there a response" but "can I retry
 * this", and that is `isTransport` and `status`.
 */
export class ApiError extends Error {
  readonly problem: Problem;
  /** True when no HTTP response was received at all (offline, DNS, abort). */
  readonly isTransport: boolean;
  /** Seconds, from `Retry-After`, when the server said how long to wait. */
  readonly retryAfterSeconds: number | undefined;
  /**
   * `RateLimit-Degraded: true` was on the response.
   *
   * The server sets it when the rate limiter could not reach its counter store
   * and the policy is `FailClosed` — sign-in among them. The user is then being
   * refused *because an abuse control is down*, not because they tried too
   * often, and the two deserve different words. Verified against a running
   * server with its cache switched off: every sign-in returns 429 with this
   * header until the cache comes back.
   */
  readonly isDegraded: boolean;

  constructor(
    problem: Problem,
    options?: { isTransport?: boolean; retryAfterSeconds?: number; isDegraded?: boolean },
  ) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
    this.isTransport = options?.isTransport ?? false;
    this.retryAfterSeconds = options?.retryAfterSeconds;
    this.isDegraded = options?.isDegraded ?? false;
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string {
    return this.problem.code;
  }

  get traceId(): string | undefined {
    return this.problem.trace_id;
  }

  /** The field-level complaints keyed by pointer or parameter, for attaching to inputs. */
  fieldErrors(): Map<string, string> {
    const out = new Map<string, string>();
    for (const error of this.problem.errors ?? []) {
      const key = error.pointer ?? error.parameter;
      if (key) out.set(key, error.detail ?? error.code ?? 'This value was not accepted.');
    }
    return out;
  }
}

/**
 * Whether a response body is shaped like a problem document.
 *
 * Checked structurally rather than trusted from the `Content-Type`, because a
 * proxy, a load balancer or a WAF can answer on the API's behalf with an HTML
 * error page and the wrong header, and `problem.title` reading `<!DOCTYPE html>`
 * in a toast is a worse outcome than the generic message.
 */
export function isProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.type === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.status === 'number' &&
    typeof candidate.code === 'string'
  );
}

/** Build a problem for a response whose body was not one. */
export function syntheticProblem(
  status: number,
  detail: string,
  code = 'unexpected_response',
): Problem {
  return { type: 'about:blank', title: 'Unexpected response', status, code, detail };
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** The session is gone or was never established. */
export function isUnauthenticated(error: unknown): boolean {
  return isApiError(error) && error.status === 401;
}

/**
 * The session is authenticated but has not cleared its second factor.
 *
 * `internal/api/auth.go` refuses *every* route except `POST /v1/auth/mfa/verify`
 * and `POST /v1/auth/sign-out` for such a session, with this code and a 403.
 * That makes it a routing signal rather than an error to show, and it has to be
 * told apart from an ordinary 403 by the code — branching on the status alone
 * would send a permission denial to the challenge screen.
 */
export function isMFARequired(error: unknown): boolean {
  return isApiError(error) && error.code === ProblemCode.MFARequired;
}

/* ====================================================================== *
 * The error-handling contract
 * ====================================================================== */

/** How the interface treats one failure. */
export interface Failure {
  /** A short heading, invariant for the class. Never the server's `title` for a 5xx. */
  title: string;
  /** One sentence for a person. Safe to display. */
  message: string;
  /** The tone the surface showing this should use. */
  tone: 'danger' | 'warning' | 'neutral';
  /** Whether offering a retry makes sense at all. */
  retryable: boolean;
  /** True when the right response is to re-establish the session, not to show a message. */
  reauthenticate: boolean;
  /** The trace id, when there is one. Shown beside the message, never inside it. */
  traceId: string | undefined;
}

/** "45 seconds", "13 minutes", "2 hours" — never "774 seconds". */
export function approximateDuration(total: number): string {
  if (total < 60) {
    const whole = Math.max(Math.round(total), 1);
    return `${String(whole)} second${whole === 1 ? '' : 's'}`;
  }
  const minutes = Math.round(total / 60);
  if (minutes < 60) return `${String(minutes)} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `${String(hours)} hour${hours === 1 ? '' : 's'}`;
}

/**
 * Map any failure to the one treatment it gets.
 *
 * One function, so the product cannot drift into showing a 403 one way on one
 * page and another way on the next. The rules it encodes, and why:
 *
 * - **A 5xx never shows the server's own words.** The server already replaces
 *   an internal error's detail with a fixed generic string, because *"that
 *   string was written for an operator, not for the internet"*
 *   (`internal/platform/httpx/problem.go`). This keeps that promise on the
 *   client side, where an upstream proxy's phrasing could still arrive.
 * - **401 is not a message, it is a transition.** `reauthenticate` is what the
 *   session layer acts on. Rendering "Authentication is required" inside a page
 *   leaves somebody reading an error in a shell they are no longer entitled to —
 *   which is exactly what a real expired session did before this existed.
 * - **429 is never worded as a server fault**, and a *degraded* 429 is worded
 *   differently again: that one means an abuse control is unavailable, which
 *   the user cannot fix by waiting out a quota they never spent.
 * - **404 says what the server says and no more.** "Does not exist, or you do
 *   not have access to it" is deliberately one answer; narrowing it here would
 *   hand a prober the distinction the server withholds.
 */
export function describeFailure(error: unknown): Failure {
  const traceId = isApiError(error) ? error.traceId : undefined;
  const base = { tone: 'danger', retryable: false, reauthenticate: false, traceId } as const;

  if (!isApiError(error)) {
    return {
      ...base,
      title: 'Something went wrong',
      message: error instanceof Error ? error.message : 'An unexpected error occurred.',
    };
  }

  if (error.isTransport) {
    return {
      ...base,
      title: 'Cannot reach Exi Git',
      message: 'The request did not complete. Check your connection and try again.',
      retryable: true,
    };
  }

  const detail = error.problem.detail;

  switch (error.status) {
    case 400:
    case 422:
      return {
        ...base,
        title: 'That request was not accepted',
        message: detail ?? 'Some of the values sent were not valid.',
      };

    case 401:
      return {
        ...base,
        title: 'Your session has ended',
        message: 'Sign in again to continue.',
        reauthenticate: true,
      };

    case 403:
      if (isMFARequired(error)) {
        return {
          ...base,
          title: 'A second factor is required',
          message: 'Enter the code from your authenticator to continue.',
          reauthenticate: true,
        };
      }
      return {
        ...base,
        title: 'Not permitted',
        // The server's detail here is written for a person and is specific,
        // including the Origin/Sec-Fetch-Site refusal — worth showing, because
        // it means something is wrong with how the application was loaded
        // rather than with what the user asked for.
        message: detail ?? 'You do not have permission to do that.',
      };

    case 404:
      return {
        ...base,
        title: 'Not found',
        message:
          detail ?? 'The requested resource does not exist, or you do not have access to it.',
        tone: 'neutral',
      };

    case 409:
      return {
        ...base,
        title: 'That conflicts with the current state',
        message: detail ?? 'Something else changed first. Reload and try again.',
        retryable: true,
      };

    case 412:
      return {
        ...base,
        title: 'This was edited somewhere else',
        message: detail ?? 'Reload to pick up the current version, then try again.',
        retryable: true,
      };

    case 429:
      if (error.isDegraded) {
        return {
          ...base,
          title: 'Temporarily unavailable',
          message:
            'Exi Git is refusing requests here while one of its protections is unavailable. This is not something you did; try again shortly.',
          tone: 'warning',
          retryable: true,
        };
      }
      return {
        ...base,
        title: 'Too many attempts',
        message:
          error.retryAfterSeconds === undefined
            ? 'You have made too many requests. Wait a moment and try again.'
            : `You have made too many requests. Try again in about ${approximateDuration(error.retryAfterSeconds)}.`,
        tone: 'warning',
      };

    case 501:
      return {
        ...base,
        title: 'Not available yet',
        message: detail ?? 'Exi Git does not support that yet.',
        tone: 'neutral',
      };

    case 503:
      return {
        ...base,
        title: 'Exi Git is unavailable',
        message: 'The service is temporarily unavailable. Try again shortly.',
        retryable: true,
      };

    default:
      if (error.status >= 500) {
        return {
          ...base,
          title: 'Something went wrong on our side',
          message: 'The request could not be completed. The trace below identifies it in our logs.',
          retryable: true,
        };
      }
      return {
        ...base,
        title: 'That request was not accepted',
        message: detail ?? error.problem.title,
      };
  }
}

/**
 * One sentence for a person.
 *
 * A thin reading of `describeFailure`, so there is exactly one place deciding
 * what a failure says.
 */
export function humanize(error: unknown): string {
  return describeFailure(error).message;
}
